import { s } from "@kosmod/pancake";
import { useEffect, useMemo, useRef, useState } from "@lukekaalim/act"

export type Vector<X extends 2 | 3> = X extends 2 ? { x: number, y: number } : { x: number, y: number, z: number };

export const span = (start: DOMHighResTimeStamp, length: number, now: DOMHighResTimeStamp) => {
  if (length === 0)
    return now < start ? 0 : 1;
  return Math.min(1, Math.max(0, (now - start) / length)); 
}

export const lerp = (a: number, b: number, t: number) => a + (t * (b - a));

export const bezier3 = (a: number, b: number, c: number, t: number) => {
  return lerp(
    lerp(a, b, t),
    lerp(b, c, t),
    t,
  );
}

export const bezier4 = (a: number, b: number, c: number, d: number, t: number) => {
  return lerp(
    bezier3(a, b, c, t),
    bezier3(b, c, d, t),
    t,
  )
}

export type Interpolate<T> = (values: T[], t: number) => T;

export type Animation<T> = {
  span: { start: DOMHighResTimeStamp, length: number },
  values: T[],
  interpolate: Interpolate<T>
}
export const Animation = <T>(start: DOMHighResTimeStamp, length: number, values: T[], interpolate: Interpolate<T>): Animation<T> => ({
  span: { start, length },
  values,
  interpolate,
})

export const getAnimationPoint = <T>(animation: Animation<T>, now: DOMHighResTimeStamp) => {
  const t = span(animation.span.start, animation.span.length, now);
  return animation.interpolate(animation.values, t);
}

export const useAnimation = <T>(animation: Animation<T>, onFrame: (drivenValue: T, t: number) => void) => {
  useEffect(() => {
    const animate = (time: DOMHighResTimeStamp) => {
      const t = Math.min(1, Math.max(0, (time - animation.span.start) / animation.span.length));

      const drivenValue = animation.interpolate(animation.values, t);
      onFrame(drivenValue, t);
      if (t < 1)
        raf = requestAnimationFrame(animate);
    };
    let raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
    }
  }, [animation, onFrame]);
}

export const Vector2 = {
  def: s.o({ x: s.num, y: s.num }),
  
  interpolate(points: Vector<2>[], t: number): Vector<2> {
    switch (points.length) {
      case 1:
        return points[0];
      case 2:
        return {
          x: lerp(points[0].x, points[1].x, t),
          y: lerp(points[0].y, points[1].y, t)
        }
      case 3:
        return {
          x: bezier3(points[0].x, points[1].x, points[2].x, t),
          y: bezier3(points[0].y, points[1].y, points[2].y, t),
        }
      case 4:
        return {
          x: bezier4(points[0].x, points[1].x, points[2].x, points[3].x, t),
          y: bezier4(points[0].y, points[1].y, points[2].y, points[3].y, t),
        }
      default:
        throw new Error(`Unsupported number of elements in Vector2 interpolate function`)
    }
  }
}

export const useAnimatedNumber = (value: number, speed: number = 200) => {
  const animatedRef = useRef(() => Animation(
    0, 0,
    [value, value, value, value],
    (values, t) => bezier4(values[0], values[1], values[2], values[3], t))
  );

  const animation = useMemo(() => {
    const now = performance.now();
    const current = getAnimationPoint(animatedRef.get(), now);
    animatedRef.set(Animation(now, speed, [current, value, value, value], animatedRef.get().interpolate))
    return animatedRef.get();
  }, [value])

  return animation
}